// 208 §1 — `import React`: the house remedy for react/react-in-jsx-scope (SharedUI.tsx says why), paid by LoadedOnUse's element
import React, { useEffect, useMemo, useState } from "react";
import {
  Trash2,
  Pencil,
  Link2,
  ExternalLink,
  StickyNote,
  X,
} from "lucide-react";
import { api } from "../api";
import { loadOneDrivePicker } from "../lib/loadOnUse.mjs"; // 208 §1 — the browse pane is the picker's, loaded on use
import { SectionHeader, AddButton, LockButton, LoadedOnUse } from "../components/SharedUI";
import { useTranslation } from "../i18n";
import { orderParties } from "../lib/memberOrder"; // 216 §4 [RULED 71] — the clients' rows by last name
import { CT, RAD, FS, Z } from "../lib/tokens";
import { useConfirm } from "../components/useConfirmAndUndo";
import { PromptDialog } from "../components/SharedUI";
import Table from "../components/Table";

function clientLabel(c: any) {
  if (!c) return "";
  if (c.name && c.name.trim()) return c.name;
  const fn = (c.firstName || "").trim();
  const ln = (c.lastName || "").trim();
  return [fn, ln].filter(Boolean).join(" ") || `Client #${c.id}`;
}

export default function DocTrackingMatrix({
  collectiveCaseId,
  clients: clientsIn = [],
  canManageTypes = false,
  collapsible = false,
  sectionOpen = true,
  onSectionToggle,
  toast,
}: any) {
  const { t } = useTranslation();
  const askConfirm = useConfirm();
  // 216 §4 [RULED 71] — the rows in the one order (the collective case hands them in its stored order)
  const clients = orderParties(clientsIn as readonly { id: number }[]);
  // 157 §2 — this file held FIVE of the eight native dialogs the brief counted
  // here, and none of the five was the yes/no gate the brief described: they
  // were alert() error reports. An error report is a toast in this app, so the
  // component takes one. `promptDlg` drives the two name prompts.
  const [promptDlg, setPromptDlg] = useState<{
    title: string;
    initialValue: string;
    onSubmit: (value: string) => void;
  } | null>(null);
  const fail = (e: { message?: string } | null | undefined) =>
    toast?.(
      "⚠ " + (e?.message || t("common.failed", { fallback: "Failed" })),
      "error",
    );
  const [types, setTypes] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<any>(null);
  // Phase 23 — one combined editor (link + note) opened by the row pencil, plus
  // a read-only popover shown when the note icon is clicked.
  const [editModal, setEditModal] = useState<any>(null); // { clientId, documentTypeId, link, note }
  const [viewNote, setViewNote] = useState<any>(null); // { note, client, type }
  // Phase 25.1 B.5: section lock — ephemeral, defaults locked. Unlocking reveals
  // the "+ Add Type" modify tool; the spine (SectionHeader) hides this lock when
  // the section is collapsed.
  const [locked, setLocked] = useState(true);
  /* BRIEF 108 §5 — this pane mounts inside CCV, which stays mounted across
     matter switches, so the lock survived them. Re-lock when the matter
     changes, like every other pane lock. */
  useEffect(() => {
    setLocked(true);
  }, [collectiveCaseId]);

  const statusIndex = useMemo(() => {
    const m = new Map();
    for (const s of statuses) m.set(`${s.clientId}:${s.documentTypeId}`, s);
    return m;
  }, [statuses]);

  async function load() {
    if (!collectiveCaseId) return;
    setLoading(true);
    setErr(null);
    try {
      const data = (await api.get(
        `/collective-cases/${collectiveCaseId}/document-tracking`,
      )) as { types?: unknown[]; statuses?: unknown[] };
      setTypes(data.types || []);
      setStatuses(data.statuses || []);
    } catch (e: any) {
      setErr(
        e.message ||
          t("docTracking.failed_to_load", { fallback: "Failed to load" }),
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectiveCaseId]);

  // Phase 30 (C) — live convergence on 'ccdt' pokes for this CC.
  useEffect(() => {
    let tmr: any = null;
    const h = (e: any) => {
      const d = e?.detail;
      if (!d || d.type !== "ccdt" || Number(d.ccId) !== Number(collectiveCaseId)) return;
      clearTimeout(tmr);
      tmr = setTimeout(load, 400);
    };
    window.addEventListener("lx:sse", h);
    return () => {
      clearTimeout(tmr);
      window.removeEventListener("lx:sse", h);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectiveCaseId]);

  function addType() {
    setPromptDlg({
      title: t("docTracking.prompt_new_type", {
        fallback: 'Document type name (e.g. "ID copy", "Power of Attorney"):',
      }),
      initialValue: "",
      onSubmit: async (name: string) => {
        setPromptDlg(null);
        try {
          const c = await api.post(
            `/collective-cases/${collectiveCaseId}/document-types`,
            { name },
          );
          setTypes((p) => [...p, c]);
        } catch (e: any) {
          fail(e);
        }
      },
    });
  }
  function renameType(tt: any) {
    setPromptDlg({
      title: t("docTracking.prompt_rename_type", {
        fallback: "Rename document type:",
      }),
      initialValue: tt.name,
      onSubmit: async (name: string) => {
        setPromptDlg(null);
        // Unchanged name is the no-op window.prompt's caller checked for.
        if (name === tt.name) return;
        try {
          const u = await api.put(
            `/collective-cases/${collectiveCaseId}/document-types/${tt.id}`,
            { name },
          );
          setTypes((p) => p.map((x) => (x.id === tt.id ? u : x)));
        } catch (e: any) {
          fail(e);
        }
      },
    });
  }
  function deleteType(tt: any) {
    askConfirm({
      // 161C §3 — the column is named, so the title names it.
      title: t("confirm.delete_doc_type_title", {
        name: tt.name,
        fallback: `Delete ${tt.name}?`,
      }),
      message: t("docTracking.confirm_delete_type", {
        name: tt.name,
        fallback: `Delete "${tt.name}"? All data for this column will be lost.`,
      }),
      confirmLabel: t("common.delete", { fallback: "Delete" }),
      onConfirm: async () => {
        try {
          await api.del(
            `/collective-cases/${collectiveCaseId}/document-types/${tt.id}`,
          );
          setTypes((p) => p.filter((x) => x.id !== tt.id));
          setStatuses((p) => p.filter((s) => s.documentTypeId !== tt.id));
        } catch (e: any) {
          fail(e);
        }
      },
    });
  }

  async function upsertStatus(patch: any) {
    const saved = (await api.put(
      `/collective-cases/${collectiveCaseId}/document-status`,
      patch,
    )) as { clientId: number; documentTypeId: number } & Record<string, unknown>;
    setStatuses((prev) => {
      const i = prev.findIndex(
        (s) =>
          s.clientId === saved.clientId &&
          s.documentTypeId === saved.documentTypeId,
      );
      if (i === -1) return [...prev, saved];
      const n = prev.slice();
      n[i] = saved;
      return n;
    });
  }
  async function toggleReceived(clientId: any, documentTypeId: any) {
    const cur = statusIndex.get(`${clientId}:${documentTypeId}`);
    try {
      await upsertStatus({
        clientId,
        documentTypeId,
        received: !(cur && cur.received),
      });
    } catch (e: any) {
      fail(e);
    }
  }
  // Phase 23 — the combined editor saves both link and note in a single PUT.
  // Empty string clears the column (server maps "" → NULL). The note column
  // already exists (migration 20260602000000); persistence round-trips through
  // upsertStatus → reload().
  async function saveEdit({ link, note }: any) {
    if (!editModal) return;
    try {
      await upsertStatus({
        clientId: editModal.clientId,
        documentTypeId: editModal.documentTypeId,
        link: link || "",
        note: note || "",
      });
      setEditModal(null);
    } catch (e: any) {
      fail(e);
    }
  }

  const perType = useMemo(() => {
    const o: Record<string, any> = {};
    for (const t of types) {
      let n = 0;
      for (const c of clients) {
        const s = statusIndex.get(`${c.id}:${t.id}`);
        if (s && s.received) n++;
      }
      o[t.id] = n;
    }
    return o;
  }, [types, clients, statusIndex]);
  const perClient = useMemo(() => {
    const o: Record<string, any> = {};
    for (const c of clients) {
      let n = 0;
      for (const t of types) {
        const s = statusIndex.get(`${c.id}:${t.id}`);
        if (s && s.received) n++;
      }
      o[c.id] = n;
    }
    return o;
  }, [types, clients, statusIndex]);

  return (
    <div>
      <SectionHeader
        {...({
        title: t("docTracking.title", { fallback: "Document Tracking" }),
        collapsible: collapsible,
        open: sectionOpen,
        onToggle: onSectionToggle,
        style: { margin: "10px 0 6px" },
        rightContent:
          canManageTypes && (
            <>
              <AddButton
                {...({
                label: t("docTracking.add_type", {
                  fallback: "Add document request",
                }),
                onClick: addType,
                visible: !locked,
              } as any)}
              />
              <LockButton
                {...({
                locked: locked,
                onToggle: () => setLocked((v: any) => !v),
              } as any)}
              />
            </>
          )
        } as any)}
      />
      {(!collapsible || sectionOpen) && (
        <>
          {err && <p style={{ color: "var(--rd)", fontSize: FS.s11 }}>{err}</p>}
          {loading && (
            <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
              {t("common.loading", { fallback: "Loading…" })}
            </p>
          )}
          {!loading && types.length === 0 && (
            <p style={{ fontSize: FS.s11, color: "var(--tm)", marginBottom: 8 }}>
              {t("docTracking.empty", {
                fallback: "No document types configured.",
              })}
              {canManageTypes
                ? t("docTracking.empty_admin_hint", {
                    fallback: ' Use "+ Add document request" to define columns.',
                  })
                : ""}
            </p>
          )}
          {!loading && types.length > 0 && (
            <div className="table-wrap">
              <Table
                style={{
                  borderCollapse: "collapse",
                  fontSize: FS.s11,
                  width: "100%",
                  // Phase 25.1 C3: natural-width columns (auto layout). When many
                  // request columns overflow, the wrapper scrolls horizontally;
                  // the Client (left) and Total (right) columns are pinned via
                  // sticky positioning so they stay visible. Columns never
                  // squeeze, truncate, or hide.
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "6px 8px",
                        borderBottom: "1px solid var(--br)",
                        position: "sticky",
                        left: 0,
                        zIndex: Z.stickyHead,
                        background: "var(--bs)",
                        // Phase 25.1 C3: pinned left at natural width.
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("common.client", { fallback: "Client" })}
                    </th>
                    {types.map((tt) => (
                      <th
                        key={tt.id}
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid var(--br)",
                          textAlign: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <span>{tt.name}</span>
                          {canManageTypes && (
                            <>
                              <button
                                title={t("common.rename", {
                                  fallback: "Rename",
                                })}
                                onClick={() => renameType(tt)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "var(--tm)",
                                  padding: 0,
                                }}
                              >
                                <Pencil size={11} />
                              </button>
                              <button
                                title={t("common.delete", {
                                  fallback: "Delete",
                                })}
                                onClick={() => deleteType(tt)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "var(--rd)",
                                  padding: 0,
                                }}
                              >
                                <Trash2 size={11} />
                              </button>
                            </>
                          )}
                        </span>
                      </th>
                    ))}
                    <th
                      style={{
                        padding: "6px 8px",
                        borderBottom: "1px solid var(--br)",
                        textAlign: "center",
                        // Phase 25.1 C3: pinned to the right edge so it stays
                        // visible while the request columns scroll horizontally.
                        position: "sticky",
                        right: 0,
                        zIndex: Z.stickyHead,
                        background: "var(--bs)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("common.total", { fallback: "Total" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c: any) => (
                    <tr key={c.id}>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid var(--bc)",
                          position: "sticky",
                          left: 0,
                          background: "var(--bs)",
                        }}
                      >
                        {clientLabel(c)}
                      </td>
                      {types.map((tt) => {
                        const s = statusIndex.get(`${c.id}:${tt.id}`);
                        const rcv = !!(s && s.received);
                        const hl = !!(s && s.link);
                        const hn = !!(s && s.note);
                        return (
                          <td
                            key={tt.id}
                            style={{
                              padding: "4px 8px",
                              borderBottom: "1px solid var(--bc)",
                              textAlign: "center",
                              background: rcv
                                ? "rgba(52,211,153,0.15)"
                                : "transparent",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={rcv}
                                onChange={() => toggleReceived(c.id, tt.id)}
                              />
                              {/* Phase 23 — link icon: opens the link when set,
                                  themed; faded indicator when empty. */}
                              {hl ? (
                                <a
                                  href={s.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={t("docTracking.open_link", {
                                    fallback: "Open link",
                                  })}
                                  style={{
                                    color: "var(--ac)",
                                    display: "inline-flex",
                                  }}
                                >
                                  <ExternalLink size={12} />
                                </a>
                              ) : (
                                <span
                                  title={t("docTracking.no_link", {
                                    fallback: "No link",
                                  })}
                                  style={{
                                    color: "var(--tm)",
                                    opacity: 0.4,
                                    display: "inline-flex",
                                  }}
                                >
                                  <Link2 size={12} />
                                </span>
                              )}
                              {/* Phase 23 — note icon: shows the note when set,
                                  themed; faded indicator when empty. */}
                              {hn ? (
                                <button
                                  title={t("docTracking.view_note", {
                                    fallback: "View note",
                                  })}
                                  onClick={() =>
                                    setViewNote({
                                      note: s.note,
                                      client: clientLabel(c),
                                      type: tt.name,
                                    })
                                  }
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "var(--ac)",
                                    padding: 0,
                                    display: "inline-flex",
                                  }}
                                >
                                  <StickyNote size={12} />
                                </button>
                              ) : (
                                <span
                                  title={t("docTracking.no_note", {
                                    fallback: "No note",
                                  })}
                                  style={{
                                    color: "var(--tm)",
                                    opacity: 0.4,
                                    display: "inline-flex",
                                  }}
                                >
                                  <StickyNote size={12} />
                                </span>
                              )}
                              {/* Phase 23 — single edit pencil at the right end of
                                  the row opens one modal holding link + note. */}
                              <button
                                title={t("docTracking.edit_row", {
                                  fallback: "Edit link & note",
                                })}
                                onClick={() =>
                                  setEditModal({
                                    clientId: c.id,
                                    documentTypeId: tt.id,
                                    link: (s && s.link) || "",
                                    note: (s && s.note) || "",
                                  })
                                }
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "var(--tm)",
                                  padding: 0,
                                  display: "inline-flex",
                                }}
                              >
                                <Pencil size={11} />
                              </button>
                            </span>
                          </td>
                        );
                      })}
                      <td
                        style={{
                          padding: "4px 8px",
                          borderBottom: "1px solid var(--bc)",
                          textAlign: "center",
                          color: "var(--tm)",
                          // Phase 25.1 C3: pinned right (matches header).
                          position: "sticky",
                          right: 0,
                          background: "var(--bs)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {perClient[c.id] || 0}/{types.length}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      style={{
                        padding: "6px 8px",
                        borderTop: "1px solid var(--br)",
                        fontWeight: 600,
                        position: "sticky",
                        left: 0,
                        background: "var(--bs)",
                      }}
                    >
                      {t("docTracking.received", { fallback: "Received" })}
                    </td>
                    {types.map((tt) => (
                      <td
                        key={tt.id}
                        style={{
                          padding: "6px 8px",
                          borderTop: "1px solid var(--br)",
                          textAlign: "center",
                          color: "var(--tm)",
                        }}
                      >
                        {perType[tt.id] || 0}/{clients.length}
                      </td>
                    ))}
                    <td
                      style={{
                        padding: "6px 8px",
                        borderTop: "1px solid var(--br)",
                        // Phase 25.1 C3: pinned right (matches header).
                        position: "sticky",
                        right: 0,
                        background: "var(--bs)",
                      }}
                    />
                  </tr>
                </tfoot>
              </Table>
            </div>
          )}
          {/* Phase 23 — combined link + note editor (one modal, browse pane
              embedded inline; no more stacked modals). */}
          {editModal && (
            <div
              onClick={() => setEditModal(null)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: Z.portal,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "var(--bs)",
                  border: "1px solid var(--br)",
                  borderRadius: RAD.r8,
                  padding: 16,
                  width: 640,
                  maxWidth: "94vw",
                  maxHeight: "88vh",
                  overflow: "auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <h4 style={{ margin: 0, fontSize: FS.s13 }}>
                    {t("docTracking.edit_row_title", {
                      fallback: "Document link & note",
                    })}
                  </h4>
                  <button
                    onClick={() => setEditModal(null)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--tm)",
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
                <DocRowEditor
                  initialLink={editModal.link || ""}
                  initialNote={editModal.note || ""}
                  ccId={collectiveCaseId}
                  onSave={saveEdit}
                  onCancel={() => setEditModal(null)}
                />
              </div>
            </div>
          )}
          {/* Phase 23 — read-only note popover (note icon click just shows it). */}
          {viewNote && (
            <div
              onClick={() => setViewNote(null)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: Z.portal,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "var(--bs)",
                  border: "1px solid var(--br)",
                  borderRadius: RAD.r8,
                  padding: 16,
                  width: 420,
                  maxWidth: "90vw",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <h4 style={{ margin: 0, fontSize: FS.s13 }}>
                    {viewNote.client}
                    {viewNote.type ? ` — ${viewNote.type}` : ""}
                  </h4>
                  <button
                    onClick={() => setViewNote(null)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--tm)",
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
                <p
                  style={{
                    whiteSpace: "pre-wrap",
                    fontSize: FS.s12,
                    color: "var(--tp)",
                    margin: 0,
                  }}
                >
                  {viewNote.note}
                </p>
              </div>
            </div>
          )}
        </>
      )}
      {/* 157 §2 — the two window.prompt name dialogs, now the app's own. */}
      {promptDlg && (
        <PromptDialog
          title={promptDlg.title}
          initialValue={promptDlg.initialValue}
          onSubmit={promptDlg.onSubmit}
          onCancel={() => setPromptDlg(null)}
        />
      )}
    </div>
  );
}

// Phase 23 — combined editor: a link field, an inline browse pane (the same
// confinement-enforcing OneDriveBrowserPane the standalone picker used, scoped
// by ccId — embedding does not relax confinement), and a free-text note.
// Saving writes both columns in one PUT; emptying a field clears that column.
function DocRowEditor({ initialLink, initialNote, ccId, onSave, onCancel }: any) {
  const { t } = useTranslation();
  const [link, setLink] = useState(initialLink);
  const [note, setNote] = useState(initialNote);
  return (
    <>
      <div style={{ marginBottom: 10 }}>
        <label
          style={{
            fontSize: FS.s11,
            color: "var(--tm)",
            display: "block",
            marginBottom: 4,
          }}
        >
          {t("docTracking.link_label", { fallback: "Link" })}
        </label>
        <input
          className="field-input"
          autoFocus
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="https://..."
          style={{ width: "100%" }}
        />
      </div>
      {/* Inline browse pane — picking a file/folder fills the link field above. */}
      <div
        style={{
          border: "1px solid var(--br)",
          borderRadius: RAD.r6,
          marginBottom: 12,
          overflow: "hidden",
        }}
      >
        {/* 208 §1 — the pane is the OneDrive picker's: it loads when this editor opens */}
        <LoadedOnUse load={loadOneDrivePicker} inline>
        {(m) => (
        <m.OneDriveBrowserPane
          {...({
            embedded: true,
            ccId: ccId || null,
            onPick: (item: any) => {
              if (item && item.webUrl) setLink(item.webUrl);
            },
            onCancel: () => {},
          } as any)}
        />
        )}
        </LoadedOnUse>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label
          style={{
            fontSize: FS.s11,
            color: "var(--tm)",
            display: "block",
            marginBottom: 4,
          }}
        >
          {t("docTracking.note_label", { fallback: "Note" })}
        </label>
        <textarea
          className="field-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("docTracking.note_placeholder", {
            fallback: "Free-text note for this row…",
          })}
          rows={3}
          style={{ width: "100%", resize: "vertical" }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          onClick={onCancel}
          style={{
            background: "none",
            border: "1px solid var(--br)",
            color: "var(--tp)",
            borderRadius: RAD.r4,
            padding: "4px 10px",
            cursor: "pointer",
            fontSize: FS.s11,
          }}
        >
          {t("common.cancel", { fallback: "Cancel" })}
        </button>
        <button
          onClick={() => onSave({ link: link.trim(), note: note.trim() })}
          style={{
            background: "var(--ac)",
            border: "none",
            color: CT.c_fff,
            borderRadius: RAD.r4,
            padding: "4px 10px",
            cursor: "pointer",
            fontSize: FS.s11,
          }}
        >
          {t("common.save", { fallback: "Save" })}
        </button>
      </div>
    </>
  );
}
